<template>
    <div class="widget-component">
        <v-container fluid>
            <v-container class="container-width">
                <v-row>
                    <v-col cols="12">
                        <v-card
                            :loading="loading"
                            class="mx-auto my-12"
                            :width="normalizedOptions.card.width"
                            :elevation="normalizedOptions.card.elevation"
                        >
                            <v-card-text>
                                <v-row
                                    align="center"
                                    class="mx-0"
                                >
                                    <v-col cols="12" class="px-lg-16 py-lg-16">
                                        <v-col cols="12">
                                            <div v-if="normalizedOptions.register.show">
                                                <p>
                                                    {{ normalizedOptions.register.text }}
                                                    <a :href="normalizedOptions.register.url"
                                                       :style="{ color: `${normalizedOptions.register.linkColour}` }">
                                                        {{ normalizedOptions.register.linkText }}
                                                    </a>
                                                </p>
                                            </div>
                                        </v-col>
                                        <v-col cols="12">
                                            <h1 class="mb-6 color-primary">{{ normalizedOptions.title }}</h1>
                                        </v-col>

                                        <v-col
                                            cols="12"
                                            v-if="normalizedOptions.intro.show"
                                        >
                                            <div v-html="normalizedOptions.intro.textHtml"></div>
                                        </v-col>

                                        <v-col cols="12">
                                            <label
                                                for="emailField"
                                                v-if="!normalizedOptions.fields.email.inlineLabels"
                                                class="mb-4 font-weight-bold"
                                            >
                                                {{ normalizedOptions.fields.email.label }}
                                            </label>
                                            <v-text-field
                                                id="emailField"
                                                autofocus
                                                v-model="loginEmail"
                                                :label="normalizedOptions.fields.email.inlineLabels ? normalizedOptions.fields.email.label : ''"
                                                outlined
                                                hide-details="auto"
                                                class="rounded-0 mb-4"
                                                required
                                                :error-messages="getError('email')"
                                            ></v-text-field>
                                            <span v-if="showCreateAccountMessage">
                                              <v-alert type="error">
                                                To manage your account, please
                                                    <a :href="normalizedOptions.register.url">
                                                        sign up
                                                    </a>
                                                first
                                              </v-alert>
                                            </span>
                                        </v-col>

                                        <v-col cols="12">
                                            <label
                                                for="passwordField"
                                                v-if="!normalizedOptions.fields.password.inlineLabels"
                                                class="mb-4 font-weight-bold"
                                            >
                                                {{ normalizedOptions.fields.password.label }}
                                            </label>
                                            <v-text-field
                                                id="passwordField"
                                                v-model="loginPassword"
                                                :label="normalizedOptions.fields.password.inlineLabels ? normalizedOptions.fields.password.label : ''"
                                                outlined
                                                hide-details="auto"
                                                class="rounded-0"
                                                required
                                                type="password"
                                                :type="showPassword ? 'text' : 'password'"
                                                :append-icon="normalizedOptions.fields.password.enableShowPassword ? showPassword ? 'mdi-eye' : 'mdi-eye-off' : ''"
                                                @click:append="showPassword = !showPassword"
                                                :error-messages="getError('password')"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-checkbox
                                                class="ma-0"
                                                v-model="rememberMe"
                                                :label="normalizedOptions.fields.rememberMe.label"
                                                v-if="normalizedOptions.fields.rememberMe.show"
                                            ></v-checkbox>
                                        </v-col>

                                        <v-col cols="12"
                                               v-if="errors && errors.error && errors.error == 'invalid_grant'">
                                            <v-alert type="error">
                                                Email or password incorrect
                                            </v-alert>
                                        </v-col>

                                        <v-col cols="12" md="8">
                                            <v-btn
                                                color="primary"
                                                @click="login()"
                                                class="ma-0"
                                                large
                                                block
                                                :loading="loading"
                                                :disabled="loading"
                                            >
                                                {{ normalizedOptions.fields.loginButton.text }}
                                            </v-btn>
                                        </v-col>

                                        <v-col cols="12" v-if="normalizedOptions.forgotPassword.show">
                                            <p>
                                                <a :href="normalizedOptions.forgotPassword.url"
                                                   :style="{ color: `${normalizedOptions.forgotPassword.textColour}` }">
                                                    {{ normalizedOptions.forgotPassword.text }}
                                                </a>
                                            </p>
                                        </v-col>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>
    </div>
</template>

<script>
import {defineComponent, ref, onMounted, computed} from "@vue/composition-api";
import {has, merge} from "lodash";
import authenticationState from "../state/AuthenticationState";
import AuthenticationService from "../services/AuthenticationService";

export default defineComponent({
    name: "Login",
    components: {},
    props: {
        settings: {
            type: Object
        },
        accessToken: {
            type: String
        },
        showTitle: {
            type: Boolean,
            default: true
        }
    },
    setup(props) {
        const loading = ref(false);
        const loginEmail = ref(null);
        const loginPassword = ref(null);
        const rememberMe = ref(null);
        const showPassword = ref(false);
        const errors = ref(null);
        const showCreateAccountMessage = ref(false);
        const defaultSettings = ref({
            title: 'Login',
            intro: {
                show: false,
                textHtml: '<p></p>'
            },
            card: {
                width: 622,
                elevation: 5
            },
            register: {
                show: true,
                text: 'Need an account?',
                textColour: '#000',
                linkText: 'Sign up here',
                linkColour: 'blue',
                url: '/register'
            },
            fields: {
                email: {
                    inlineLabels: false,
                    label: 'E-mail*'
                },
                password: {
                    inlineLabels: false,
                    enableShowPassword: false,
                    label: 'Password*'
                },
                rememberMe: {
                    show: true,
                    label: 'Remember me'
                },
                loginButton: {
                    show: true,
                    text: 'Login'
                }
            },
            forgotPassword: {
                show: true,
                text: 'Forgot your password?',
                textColour: '#000',
                url: '/forgot-password'
            },
            events: {
                redirectAfterLogin: '/dashboard',
                redirectIfLoggedIn: '/dashboard'
            }
        });

        const normalizedOptions = computed(() => {
            return merge(defaultSettings.value, props.settings);
        });

        function getError(field) {
            const fieldToSearch = 'errors.' + field + '[0]';
            if (has(errors.value, fieldToSearch)) {
                return errors.value.errors[field][0];
            }
            return null
        }

        function login() {
            loading.value = true;
            errors.value = null;
            showCreateAccountMessage.value = false;
            AuthenticationService.authenticate(loginEmail.value, loginPassword.value, rememberMe.value)
                .then(() => {
                    if (has(normalizedOptions.value, 'events.redirectAfterLogin')) {
                        window.location.href = normalizedOptions.value.events.redirectIfLoggedIn;
                    }
                })
                .catch(error => {
                    if(error.response.status === 409) {
                        showCreateAccountMessage.value = true;
                    }
                    else if (has(error, 'response.data')) {
                        errors.value = error.response.data;
                    }
                })
                .finally(() => {
                    loading.value = false;
                });
        }

        onMounted(() => {
            init();
        });

        function init() {
            if (authenticationState.accessToken.value) {
                if (has(normalizedOptions.value, 'events.redirectIfLoggedIn')) {
                    window.location.href = normalizedOptions.value.events.redirectIfLoggedIn;
                }
            }
            loading.value = false;
        }

        return {
            // Variables
            normalizedOptions,
            loading,
            loginEmail,
            loginPassword,
            rememberMe,
            showPassword,
            errors,
            showCreateAccountMessage,
            // Functions
            login,
            getError
        };
    }
});
</script>
